// roleAssignment.js — prescribed Thinker/Worker/Verifier role assignment (role-scaffold-twv E1-WS1-T01).
// @arch:pure-engine
//
// FORM, not MECHANISM: a FIXED, deterministic policy mapping a task turn to role→model. NO learning, NO
// evolution (RL / CMA-ES would be N3). The Verifier is NEVER a model — it is the deterministic oracle
// (see oracle.js); we return the sentinel 'oracle' so the caller keeps using verifyTask as the sole judge.
//
// PURE (doctrine [T1] arch:lib-purity): imports only the registry-derived Council roster. No react, no
// window, no fetch, no Electron. Concrete model calls are wired at the edge (SessionWorkspace).

import { COUNCIL_AGENTS } from '../councilAgents.js';

// Prescribed preference for the THINKER role: reasoning-strong providers first. Fixed by design — this is
// the "prescribed scaffold = safe" choice; an evolved/learned ordering would be N3 and is out of scope.
export const THINKER_PREFERENCE = Object.freeze([
  'anthropic', 'google', 'openai', 'deepseek', 'xai', 'mistral',
]);

// The Verifier is the deterministic oracle, never a model. This sentinel tells the caller "keep using the
// injected verifyTask (oracle honest-green) — do not route a model into the Verifier role".
export const VERIFIER_SENTINEL = 'oracle';

// E8 — providers that are THEMSELVES a Thinker→Worker→Verifier pool (a "model-of-models"): Sakana Fugu
// (TRINITY + Conductor decompose + verify internally). When such a provider is the worker, Apolo must NOT
// prepend its own Thinker (double-decomposition) — it degrades to Worker-only, and the deterministic oracle
// STAYS the sole DONE judge (no double-verification: Fugu's internal verify is the model's, the oracle's is
// structural honest-green). This is FORM, not a model claim.
export const SELF_DECOMPOSING_PROVIDERS = Object.freeze(new Set(['fugu']));

/** Provider key of a 'provider/model' id (lowercased). */
function providerOfId(modelId) {
  const id = String(modelId ?? '');
  const slash = id.indexOf('/');
  return (slash > 0 ? id.slice(0, slash) : id).toLowerCase();
}

/** Is this model served by a self-decomposing TWV pool (e.g. Fugu)? Then skip Apolo's Thinker. */
export function isSelfDecomposingProvider(modelId) {
  return SELF_DECOMPOSING_PROVIDERS.has(providerOfId(modelId));
}

// COUNCIL_AGENTS entries carry { id: 'provider/model', provider: 'Anthropic'|'Google'|... }. Derive the
// registry provider key from the id prefix (lowercased), robust to display-label casing.
function providerOf(agent) {
  const id = String(agent?.id ?? '');
  const slash = id.indexOf('/');
  if (slash > 0) return id.slice(0, slash).toLowerCase();
  return String(agent?.provider ?? '').toLowerCase();
}

/**
 * Assign roles for one task turn. PRESCRIBED + deterministic.
 *   worker   = currentModelId  (the route's chosen model stays the executor — anchors cost & behavior)
 *   thinker  = strongest DISTINCT pool model by preference; null if none distinct (→ Worker-only, today's shape)
 *   verifier = 'oracle'        (the deterministic oracle; never a model)
 *
 * @param {{ pool?: Array<{id:string,provider?:string}>, currentModelId?: string, prefer?: string[] }} [opts]
 * @returns {{ thinker: string|null, worker: string, verifier: 'oracle' }}
 */
export function assignRoles({ pool = COUNCIL_AGENTS, currentModelId = '', prefer = THINKER_PREFERENCE } = {}) {
  const worker = String(currentModelId ?? '');
  const agents = Array.isArray(pool) ? pool.filter((a) => a && a.id) : [];

  // E8 — a self-decomposing pool (Fugu) IS its own Thinker→Worker; skip Apolo's Thinker so we never
  // double-decompose. Oracle stays the Verifier (no double-verification). Worker-only = today's shape.
  if (isSelfDecomposingProvider(worker)) {
    return { thinker: null, worker, verifier: VERIFIER_SENTINEL };
  }

  let thinker = null;
  // 1) strongest distinct model whose provider is highest in the prescribed preference.
  for (const provider of prefer) {
    const match = agents.find((a) => providerOf(a) === provider && a.id !== worker);
    if (match) { thinker = match.id; break; }
  }
  // 2) fallback — first distinct model regardless of provider (still prescribed/deterministic). Yields
  //    null ONLY when the pool is empty or every entry IS the worker (→ honest degrade to Worker-only).
  if (!thinker) {
    const any = agents.find((a) => a.id !== worker);
    thinker = any ? any.id : null;
  }

  return { thinker, worker, verifier: VERIFIER_SENTINEL };
}
